import React, { useEffect } from 'react';

import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';

import { INSTALLATION_TYPES_NEEDING_ADDRESS, INSTALLATION_TYPE_OPTIONS } from '../constants';
import { InstallationType } from '../types';

export type QueueEquipmentFormValues = {
  locationsCount: string;
  tvCount: string;
  kioskCount: string;
  tabletCount: string;
  pcCount: string;
  purchaseOrderNumber: string;
  installationType: InstallationType;
  installationAddress: string;
  requestedInstallationDate: string;
  networkReady: boolean;
};

type QueueEquipmentFieldsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clearErrors: UseFormClearErrors<any>;
  purchaseOrderFile: File | null;
  onPurchaseOrderFileChange: (file: File | null) => void;
};

const DEVICE_FIELDS = [
  { name: 'tvCount', label: 'TVs' },
  { name: 'kioskCount', label: 'Kiosks' },
  { name: 'tabletCount', label: 'Tablets' },
  { name: 'pcCount', label: 'PCs', hintId: 'pc-count-hint' },
];
const DEVICE_FIELD_NAMES = DEVICE_FIELDS.map((field) => field.name);
const DEVICE_REQUIRED_MESSAGE = 'Request at least one device.';

const QueueEquipmentFields: React.FC<QueueEquipmentFieldsProps> = ({
  register,
  errors,
  watch,
  clearErrors,
  purchaseOrderFile,
  onPurchaseOrderFileChange,
}: QueueEquipmentFieldsProps) => {
  const installationType = watch('installationType') as InstallationType;

  const totalDevices = DEVICE_FIELD_NAMES.reduce(
    (total, name) => total + Number((watch(name) as string) || 0),
    0
  );
  const hasDeviceError = DEVICE_FIELD_NAMES.some((name) => errors[name]);
  const needsAddress = INSTALLATION_TYPES_NEEDING_ADDRESS.includes(installationType);

  // Spans every device input; react-hook-form 7.20 predates the `deps` option, so the
  // error would otherwise stay up after the user types a count in a sibling.
  useEffect(() => {
    if (totalDevices > 0) clearErrors(DEVICE_FIELD_NAMES);
  }, [totalDevices, clearErrors]);

  return (
    <fieldset className="mt-5">
      <legend className="form-legend">Digital queue equipment</legend>

      <div className="sm:grid-cols-3 md:grid-cols-5 grid items-end grid-cols-2 gap-3 mt-3">
        <label className="block text-sm">
          Locations
          <input
            type="number"
            min="1"
            className="input w-full"
            {...register('locationsCount', { required: 'Required.' })}
          />
          {errors.locationsCount && (
            <span className="text-xs text-red-600">{String(errors.locationsCount.message)}</span>
          )}
        </label>
        {DEVICE_FIELDS.map(({ name, label, hintId }) => (
          <label key={name} className="block text-sm">
            {label}
            <input
              type="number"
              min="0"
              aria-describedby={hintId}
              className="input w-full"
              {...register(name, {
                validate: () => totalDevices > 0 || DEVICE_REQUIRED_MESSAGE,
              })}
            />
          </label>
        ))}
      </div>
      <span id="pc-count-hint" className="block mt-1 text-xs text-gray-2.0">
        PCs: count only the ones that do not come bundled with a TV or kiosk.
      </span>
      {totalDevices === 0 && hasDeviceError && (
        <span className="text-xs text-red-600">{DEVICE_REQUIRED_MESSAGE}</span>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="block text-sm">
          Purchase order number
          <input
            className="input w-full"
            {...register('purchaseOrderNumber', {
              validate: (value: string) => !!value?.trim() || 'A purchase order is required.',
            })}
          />
          {errors.purchaseOrderNumber && (
            <span className="text-xs text-red-600">
              {String(errors.purchaseOrderNumber.message)}
            </span>
          )}
        </label>
        <label className="block text-sm">
          Purchase order evidence
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className="w-full mt-1 text-sm"
            onChange={(event) => onPurchaseOrderFileChange(event.target.files?.[0] ?? null)}
          />
          <span className="text-xs text-gray-500">
            {purchaseOrderFile
              ? purchaseOrderFile.name
              : 'Optional now — can be attached once the client signs.'}
          </span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="block text-sm">
          Installation
          <select className="input w-full" {...register('installationType')}>
            {INSTALLATION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Requested installation date
          <input type="date" className="input w-full" {...register('requestedInstallationDate')} />
        </label>
      </div>

      {needsAddress && (
        <label className="block mt-3 text-sm">
          Shipping / installation address
          <textarea
            className="input w-full"
            rows={2}
            {...register('installationAddress', {
              validate: (value: string) => !!value?.trim() || 'Provide the address.',
            })}
          />
          {errors.installationAddress && (
            <span className="text-xs text-red-600">
              {String(errors.installationAddress.message)}
            </span>
          )}
        </label>
      )}

      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" {...register('networkReady')} />
        Site already has the network and power the devices need
      </label>
    </fieldset>
  );
};

export default QueueEquipmentFields;
